import { Typography } from '@navi/web-ui/lib/primitives';
import React, { useState, useEffect } from 'react';
import styles from '../index.module.scss';
import { IRenderingEngine, TIMESCOPE } from '../interfaces/index.type';
import Checkbox from '@primitives/Checkbox/Checkbox';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store';
import { DateFormat, getFormatDate } from 'src/utils/DateHelper';
import DateTimePickerComponent from '@cp/src/components/DateTimePicker/DateTimePickerComponent';
import { DATE_TIME_TYPE } from '../../../../components/DateTimePicker/constants';

const DatePicker: React.FC<IRenderingEngine> = props => {
  const { question, register, error, source, setValue, getValue, unregister, clearErrors } = props;
  const {
    text,
    mandatory,
    defaultValue,
    referenceId,
    supportedDateType,
    maxDate,
    minDate,
    metadata
  } = question;
  const [noDateGivenFlag, setNoDateGivenFlag] = useState(false);

  const serverDate: Date = useSelector((state: RootState) => state?.common?.serverDate);
  const todayDate = new Date(serverDate).toISOString().split('T')[0];
  useEffect(() => {
    return () => {
      setValue(`${source}_${referenceId}`, undefined);
      unregister(`${source}_${referenceId}`);
      clearErrors(`${source}_${referenceId}`);
    };
  }, []);

  const min = supportedDateType === TIMESCOPE.FUTURE ? todayDate : minDate;
  const max = supportedDateType === TIMESCOPE.PAST ? todayDate : maxDate;
  const checkboxOnChangeHandler = () => {
    setValue(`${source}_${referenceId}`, null);
    setNoDateGivenFlag(prevState => !prevState);
  };

  const validateDate = (value: any) => {
    if (noDateGivenFlag) {
      return true;
    }

    if (value) {
      const date = new Date(value);
      if (date.toString() === 'Invalid Date') {
        return false;
      }
      if (min && date < new Date(min)) {
        return false;
      }
      if (max && date > new Date(max)) {
        return false;
      }

      return true;
    }

    return false;
  };

  const handleOnDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setValue(`${source}_${referenceId}`, value);
  };

  return (
    <div>
      <div key={`${source}_${referenceId}_${noDateGivenFlag}`} className={styles.input}>
        <div className={styles.label}>
          {text}
          <span className={styles.redStar}>*</span>
        </div>
        <DateTimePickerComponent
          type={DATE_TIME_TYPE.DATE}
          {...register(`${source}_${referenceId}`, {
            required: mandatory && !noDateGivenFlag,
            validate: validateDate
          })}
          id={`${source}_${referenceId}`}
          name={`${source}_${referenceId}`}
          min={!noDateGivenFlag ? min : ''}
          max={!noDateGivenFlag ? max : ''}
          containerClasses={styles.feedbackDatePicker}
          inputClasses={styles.feedbackDatePickerInput}
          iconColor={'black'}
          value={getValue(`${source}_${referenceId}`)}
          onChange={handleOnDateChange}
          disabled={noDateGivenFlag}
        />
      </div>
      {error?.[`${source}_${referenceId}`] && (
        <Typography variant="p5" className={styles.errorText}>
          {noDateGivenFlag ||
            `Please enter a valid date ` +
              (maxDate
                ? `between ${getFormatDate(
                    min || todayDate,
                    DateFormat.DD_MMM_YYYY
                  )} and ${getFormatDate(maxDate, DateFormat.DD_MMM_YYYY)}`
                : `on or after ${getFormatDate(min || todayDate, DateFormat.DD_MMM_YYYY)}`)}
        </Typography>
      )}
      {metadata?.isBlankValueAllowed ? (
        <div className={styles.input}>
          <Checkbox
            checked={noDateGivenFlag}
            onChange={checkboxOnChangeHandler}
            title="No Date given"
          />
        </div>
      ) : null}
    </div>
  );
};

export default DatePicker;
